import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import type { SearchParameters, SearchSortField } from 'lib-search-types';
import { SortBy } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { PropsWithClassName } from 'lib-shared-types-utils';

import { searchActions } from 'app/views/layout/views/search/search.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import {
  convertSortObjectToString,
  convertSortStringToObject,
} from '../../../../utils/sort/convertSort';

type SearchResultsSortProps = PropsWithClassName;

type SearchResultsSortOption = {
  value: SortBy<SearchSortField> | '';
  label: string;
};

export const sortOptions: SearchResultsSortOption[] = [
  {
    value: '',
    label: 'Relevance',
  },
  {
    value: 'updated_at:desc',
    label: 'Activity: Newest first',
  },
  {
    value: 'updated_at:asc',
    label: 'Activity: Oldest first',
  },
  {
    value: 'created_at:desc',
    label: 'Created: Newest first',
  },
  {
    value: 'created_at:asc',
    label: 'Created: Oldest first',
  },
  {
    value: 'last_message_created_at:desc',
    label: 'Last message: Newest first',
  },
  {
    value: 'last_message_created_at:asc',
    label: 'Last message: Oldest first',
  },
];

/**
 * Represents a component that is used to select a sorting option from a
 * dropdown
 */
export const SearchResultsSort = ({
  className,
}: SearchResultsSortProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { params } = useAppSelector((state) => state.search);
  const { scope } = params;

  if (scope !== 'conversations') return null;

  const value = convertSortObjectToString(params.sortBy);
  const selectedOption = sortOptions.find((option) => value === option.value);

  function handleToggleSortBy(value: SearchResultsSortOption['value']) {
    const nextParams: SearchParameters<'conversations'> = {
      scope: 'conversations',
      sortBy: convertSortStringToObject(value),
      page: 1,
    };

    dispatch(searchActions.params.update(nextParams));

    ampli.searchSortingUpdated({
      tab: 'conversations',
      type: !value ? 'relevance' : value,
    });
  }

  return (
    <FlexBox className={className} alignItems="center">
      <Text.Paragraph variant="3" subdued>
        Sort by:
      </Text.Paragraph>

      <DropdownButton
        buttonProps={{ variant: 'tertiary' }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <ButtonList>
                {sortOptions.map((option) => (
                  <ButtonList.Item
                    key={option.value}
                    isSelected={value === option.value}
                    onClick={() => {
                      if (value === option.value) return;
                      toggleDropdown();
                      handleToggleSortBy(option.value);
                    }}
                  >
                    {option.label}
                  </ButtonList.Item>
                ))}
              </ButtonList>
            )}
          </DropdownButtonContext.Consumer>
        }
        fixed
      >
        {selectedOption?.label}
      </DropdownButton>
    </FlexBox>
  );
};
